import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/Api";
import ActiveLoader from "../components/Activityloader";
import ActivityImg from "../assets/Activity.png";
import {
  ChevronLeft,
  ChevronRight,
  Smartphone,
  Key,
  Globe,
  CheckCircle2,
  XCircle,
  History,
  X,
  CalendarDays,
  Activity,
  CalendarCheck,
} from "lucide-react";

const DriverActivity = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchLoginHistory();
  }, []);

  const fetchLoginHistory = async () => {
    try {
      const regId = localStorage.getItem("driverRegNo");
      if (!regId) return;

      const response = await api.get(`/activity/login-history?regId=${regId}`);
      setLogs(response.data);
      // Optional: slight delay to prevent flicker if data loads instantly
      setTimeout(() => setLoading(false), 500);
    } catch (error) {
      console.error("Error fetching data", error);
      setLoading(false);
    }
  };

  const getLogsForDate = (date) => {
    return logs.filter((log) => {
      const logDate = new Date(log.login_datetime);
      return (
        logDate.getDate() === date.getDate() &&
        logDate.getMonth() === date.getMonth() &&
        logDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const getTodayLogs = () => {
    const today = new Date();
    return getLogsForDate(today);
  };

  const handleDateClick = (date) => {
    if (date > new Date()) return;
    setSelectedDate(date);
    setShowModal(true);
  };

  const daysInMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const renderCalendar = () => {
    const days = [];
    const totalDays = daysInMonth(currentDate);
    const startDay = firstDayOfMonth(currentDate);
    const today = new Date();

    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className="day-cell empty"></div>);
    }

    for (let d = 1; d <= totalDays; d++) {
      const dateToCheck = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        d,
      );
      const dayLogs = getLogsForDate(dateToCheck);

      const hasSuccess = dayLogs.some((l) => l.login_status === 1);
      const hasFail = dayLogs.some((l) => l.login_status === 2);
      const hasActivity = dayLogs.length > 0;

      const isToday = today.toDateString() === dateToCheck.toDateString();
      const isFuture =
        dateToCheck.setHours(0, 0, 0, 0) > today.setHours(0, 0, 0, 0);

      days.push(
        <div
          key={d}
          onClick={() => handleDateClick(dateToCheck)}
          className={`day-cell ${isToday ? "today" : ""} ${hasActivity ? "active-day" : ""} ${isFuture ? "future-day" : ""}`}
        >
          <span className="day-num">{d}</span>
          {/* {!isFuture && (
             <div className="dot-row">
                {hasSuccess && <span className="dot dott-success"></span>}
                {hasFail && <span className="dot dott-fail"></span>}
             </div>
          )} */}
        </div>,
      );
    }
    return days;
  };

  const changeMonth = (offset) => {
    const newDate = new Date(
      currentDate.setMonth(currentDate.getMonth() + offset),
    );
    setCurrentDate(new Date(newDate));
  };

  const LogItem = ({ log }) => {
    const isSuccess = log.login_status === 1;
    const isOTP = log.Login_through === 1;
    const time = new Date(log.login_datetime).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    return (
      <div className="log-item">
        <div className={`icon-box ${isSuccess ? "bg-success" : "bg-fail"}`}>
          {isOTP ? (
            <Smartphone size={20} color="white" />
          ) : (
            <Key size={20} color="white" />
          )}
        </div>
        <div className="log-details">
          <div className="log-header">
            <span className="log-title">
              {isOTP ? "OTP Login" : "MPIN Login"}
            </span>
            <span className="log-time">{time}</span>
          </div>
          <div className="log-sub">
            <span className="ip-address">
              <Globe size={11} /> {log.login_ip || "Unknown IP"}
            </span>
            <span
              className={`status-badge ${isSuccess ? "text-success" : "text-fail"}`}
            >
              {isSuccess ? "Success" : "Failed"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const todayLogs = getTodayLogs();
  if (loading) {
    return (
      <div className="loader-container">
        <ActiveLoader />
        <style>{`
            .loader-container {
                position: fixed; 
                top: 0;
                left: 0;
                width: 100%;
                height: 100%; 
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                background: #ffffff;
            }
        `}</style>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      {/* Top Header */}
      <div className="top-nav">
        <div className="nav-left">
          <Activity size={26} className="text-primary" />
          <div>
            <h2 className="page-title">Activity Log</h2>
            <span className="page-subtitle">Track sessions</span>
          </div>
        </div>

        <button
          className="attendance-btn"
          onClick={() => navigate("/attendance-history")}
        >
          <CalendarCheck size={18} />
          <span>Attendance</span>
        </button>
      </div>

      {/* Today Summary Card */}
      <div className="today-summary-card">
        <div className="today-icon-wrapper">
          <CalendarDays size={24} className="text-white" />
        </div>
        <div className="today-content">
          <span className="today-label">Logins Today</span>
          <span className="today-count">{todayLogs.length}</span>
        </div>
        <div className="today-action">
          <span className="view-text">Detailed view below</span>
        </div>
      </div>

      {/* Calendar Section */}
      <div className="calendar-container">
        <div className="month-control">
          <button className="nav-btn" onClick={() => changeMonth(-1)}>
            <ChevronLeft size={20} />
          </button>
          <div className="current-month">
            {currentDate.toLocaleString("default", { month: "long" })}
            <span>{currentDate.getFullYear()}</span>
          </div>
          <button className="nav-btn" onClick={() => changeMonth(1)}>
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="week-header">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <span key={i}>{d}</span>
          ))}
        </div>

        <div className="days-grid">{renderCalendar()}</div>
      </div>

      <div className="bottom-spacer"></div>

      {/* --- ATTRACTIVE MODAL SECTION --- */}
      {showModal && (
        <>
          <div
            className="modal-backdrop"
            onClick={() => setShowModal(false)}
          ></div>
          <div className="bottom-sheet">
            <div className="sheet-handle"></div>

            <div className="sheet-header">
              <div className="date-display">
                <div className="date-icon-box">
                  <CalendarDays size={18} className="text-primary" />
                </div>
                <div className="date-text-group">
                  <span className="date-title">Activity Details</span>
                  <span className="date-subtitle">
                    {selectedDate.toLocaleDateString(undefined, {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    })}
                  </span>
                </div>
              </div>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="sheet-content">
              {getLogsForDate(selectedDate).length === 0 ? (
                <div className="empty-state">
                  <div className="empty-illustration">
                    <History size={40} color="#CBD5E1" />
                  </div>
                  <h3>No Activity</h3>
                  <p>You didn't login on this date.</p>
                </div>
              ) : (
                <div className="logs-list">
                  {getLogsForDate(selectedDate).map((log) => (
                    <LogItem key={log.drv_lhid} log={log} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <div className="px-2">
        <img
          src={ActivityImg}
          alt="Activity Banner"
          className="w-100 rounded-4"
          style={{
            objectFit: "cover",
            height: "auto",
            display: "block",
          }}
        />
      </div>

      {/* Styles */}
      <style>{`
        :root {
            --primary: #4F46E5;
            --primary-dark: #4338ca;
            --primary-soft: #EEF2FF;
            --success: #10B981;
            --success-soft: #D1FAE5;
            --fail: #EF4444;
            --fail-soft: #FEE2E2;
            --bg: #ffffff;
            --text-dark: #1E293B;
            --text-gray: #171b22;
            --text-disabled: #CBD5E1;
        }

        .page-wrapper {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            background: var(--bg);
            min-height: 100vh;
            padding: 15px;
            box-sizing: border-box;
            animation: fadeInPage 0.4s ease-out;
        }
        
        @keyframes fadeInPage {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        /* Header */
        .top-nav { margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; }
        .nav-left { display: flex; align-items: center; gap: 12px; }
        .page-title { font-size: 20px; font-weight: 700; color: var(--text-dark); margin: 0; line-height: 1.2; }
        .page-subtitle { font-size: 13px; color: var(--text-gray); }

        .attendance-btn {
            background: var(--primary-soft); color: var(--primary); border: none;
            padding: 8px 14px; border-radius: 12px; font-size: 13px; font-weight: 600;
            display: flex; align-items: center; gap: 6px; cursor: pointer;
            transition: all 0.2s; box-shadow: 0 2px 5px rgba(0,0,0,0.03);
        }
        .attendance-btn:active { transform: scale(0.95); background: #e0e7ff; }

        /* Today Summary */
        .today-summary-card {
            background: linear-gradient(135deg, var(--primary), var(--primary-dark));
            border-radius: 20px; padding: 20px; display: flex; align-items: center; gap: 15px;
            color: white; box-shadow: 0 8px 20px rgba(79, 70, 229, 0.25); margin-bottom: 24px;
            position: relative; overflow: hidden;
        }
        .today-summary-card::after {
            content: ""; position: absolute; right: -20px; top: -20px; width: 100px; height: 100px;
            background: rgba(255,255,255,0.1); border-radius: 50%;
        }
        .today-icon-wrapper {
            width: 50px; height: 50px; background: rgba(255,255,255,0.2); border-radius: 14px;
            display: flex; align-items: center; justify-content: center;
        }
        .text-white { color: white; }
        .today-content { flex: 1; display: flex; flex-direction: column; }
        .today-label { font-size: 13px; font-weight: 500; opacity: 0.9; margin-bottom: 2px; }
        .today-count { font-size: 28px; font-weight: 700; line-height: 1; }
        .today-action { font-size: 11px; opacity: 0.8; font-style: italic; }

        /* Calendar */
        .calendar-container {
            background: white; border-radius: 24px; padding: 20px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.04); border: 1px solid #f1f5f9;
        }
        .month-control { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
        .current-month { text-align: center; font-size: 16px; font-weight: 600; color: var(--text-dark); display: flex; flex-direction: column; line-height: 1.2; }
        .current-month span { font-size: 12px; color: var(--text-gray); font-weight: 500; }
        .nav-btn { background: #f8fafc; border: none; width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: var(--text-gray); cursor: pointer; transition: 0.2s; }
        .week-header { display: grid; grid-template-columns: repeat(7, 1fr); text-align: center; margin-bottom: 12px; }
        .week-header span { font-size: 12px; color: var(--text-gray); font-weight: 600; }
        .days-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 6px; }
        .day-cell { aspect-ratio: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; border-radius: 14px; font-size: 14px; font-weight: 500; color: var(--text-dark); cursor: pointer; position: relative; }
        .day-cell.today { background: var(--primary); color: white; box-shadow: 0 4px 10px rgba(79, 70, 229, 0.3); }
        .future-day { opacity: 0.3; pointer-events: none; color: var(--text-disabled); }
        .dot-row { display: flex; gap: 3px; position: absolute; bottom: 6px; }
        .dot { width: 4px; height: 4px; border-radius: 50%; }

          /* Added .day-cell and .dot to increase specificity, and used your custom CSS variables for better UI consistency */
          .day-cell .dot.dott-success { background-color:  #00ff6a !important; }
          .day-cell .dot.dott-fail { background-color:  #ff0000  !important; }
          .day-cell.today .dot.dott-success { background-color: #ffffff !important; }

        /* --- ATTRACTIVE MODAL STYLES --- */
        
        .modal-backdrop {
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0,0,0,0.6); z-index: 2000;
            backdrop-filter: blur(4px);
            animation: fadeIn 0.3s ease-out;
        }
        
        .bottom-sheet {
            position: fixed; bottom: 0; left: 0; width: 100%;
            background: #F8FAFC; 
            z-index: 2001;
            border-top-left-radius: 32px; border-top-right-radius: 32px;
            padding: 12px 24px 40px;
            box-shadow: 0 -10px 40px rgba(0,0,0,0.15);
            animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1);
            max-height: 85%; 
            display: flex; 
            flex-direction: column;
            box-sizing: border-box;
        }

        .sheet-handle { 
            width: 40px; height: 5px; background: #CBD5E1; 
            border-radius: 10px; margin: 8px auto 20px; 
        }

        .sheet-header { 
            display: flex; justify-content: space-between; align-items: center; 
            margin-bottom: 24px; padding-bottom: 0;
        }
        
        .date-display { display: flex; align-items: center; gap: 12px; }
        .date-icon-box {
            width: 42px; height: 42px; background: white;
            border-radius: 12px; display: flex; align-items: center; justify-content: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        .date-text-group { display: flex; flex-direction: column; }
        .date-title { font-size: 18px; font-weight: 700; color: var(--text-dark); line-height: 1.1; }
        .date-subtitle { font-size: 13px; color: var(--text-gray); font-weight: 500; }

        .close-btn { 
            background: white; border: none; width: 36px; height: 36px; 
            border-radius: 50%; display: flex; align-items: center; justify-content: center; 
            cursor: pointer; color: var(--text-dark); 
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            transition: transform 0.2s;
        }
        .close-btn:active { transform: scale(0.9); }

        .sheet-content { 
            flex: 1; 
            overflow-y: auto;
            padding-bottom: 30px; 
            -webkit-overflow-scrolling: touch; 
        }
        
        /* Empty State */
        .empty-state { 
            text-align: center; padding: 40px 20px; color: var(--text-gray);
            display: flex; flex-direction: column; align-items: center;
        }
        .empty-illustration { 
            width: 80px; height: 80px; background: #EEF2FF; border-radius: 50%; 
            display: flex; align-items: center; justify-content: center; margin-bottom: 16px;
        }
        .empty-state h3 { font-size: 16px; font-weight: 600; color: var(--text-dark); margin: 0 0 6px 0; }
        .empty-state p { font-size: 14px; margin: 0; opacity: 0.8; }

        /* LOGS LIST - Native Card Style */
        .logs-list { display: flex; flex-direction: column; gap: 16px; }
        
        .log-item { 
            display: flex; align-items: center; gap: 16px; 
            padding: 10px; 
            background: white;
            border-radius: 20px; 
            box-shadow: 0 4px 15px rgba(0,0,0,0.03); /* Soft Floating Shadow */
            transition: transform 0.2s;
        }
        .log-item:active { transform: scale(0.98); }

        .icon-box { 
            width: 48px; height: 48px; border-radius: 14px; 
            display: flex; align-items: center; justify-content: center; flex-shrink: 0;
            font-size: 18px;
        }
        .bg-success { background: linear-gradient(135deg, #10B981, #059669); box-shadow: 0 4px 10px rgba(16, 185, 129, 0.2); }
        .bg-fail { background: linear-gradient(135deg, #EF4444, #DC2626); box-shadow: 0 4px 10px rgba(239, 68, 68, 0.2); }
        
        .log-details { flex: 1; }
        .log-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
        .log-title { font-size: 15px; font-weight: 700; color: var(--text-dark); letter-spacing: -0.3px; }
        .log-time { font-size: 13px; color: var(--text-gray); font-weight: 600; background: #F1F5F9; padding: 2px 8px; border-radius: 6px; }
        
        .log-sub { display: flex; justify-content: space-between; align-items: center; }
        .ip-address { font-size: 12px; color: #960707; display: flex; align-items: center; gap: 5px; font-weight: 500; }
        
        .status-badge { 
            font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
            padding: 4px 8px; border-radius: 6px;
        }
        .text-success { color: #059669; background: #ECFDF5; }
        .text-fail { color: #DC2626; background: #FEF2F2; }

        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
};

export default DriverActivity;